import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react'

export interface EnrichmentEvent {
  type:
    | 'enrichment:started'
    | 'enrichment:progress'
    | 'enrichment:completed'
    | 'enrichment:failed'
  topic: string
  data: any
  timestamp: number
}

interface EnrichmentEventsContextType {
  publishEvent: (event: Omit<EnrichmentEvent, 'timestamp'>) => void
  subscribeToSheet: (
    sheetId: string,
    callback: (event: EnrichmentEvent) => void
  ) => () => void
}

const EnrichmentEventsContext =
  createContext<EnrichmentEventsContextType | null>(null)

export function useEnrichmentEvents() {
  const context = useContext(EnrichmentEventsContext)
  if (!context) {
    throw new Error(
      'useEnrichmentEvents must be used within EnrichmentEventsProvider'
    )
  }
  return context
}

interface EnrichmentEventsProviderProps {
  children: ReactNode
}

export function EnrichmentEventsProvider({
  children,
}: EnrichmentEventsProviderProps) {
  const [subscribers, setSubscribers] = useState<
    Map<string, Set<(event: EnrichmentEvent) => void>>
  >(new Map())

  const publishEvent = useCallback(
    (event: Omit<EnrichmentEvent, 'timestamp'>) => {
      const fullEvent: EnrichmentEvent = {
        ...event,
        timestamp: Date.now(),
      }

      // Extract sheet ID from topic (e.g., "sheet:dev_sh_abc123" -> "dev_sh_abc123")
      const sheetId = event.topic.startsWith('sheet:')
        ? event.topic.replace('sheet:', '')
        : null

      if (sheetId && subscribers.has(sheetId)) {
        const sheetSubscribers = subscribers.get(sheetId)!
        sheetSubscribers.forEach((callback) => {
          try {
            callback(fullEvent)
          } catch (error) {
            console.error('Error in enrichment event callback:', error)
          }
        })
      }
    },
    [subscribers]
  )

  const subscribeToSheet = useCallback(
    (sheetId: string, callback: (event: EnrichmentEvent) => void) => {
      setSubscribers((prev) => {
        const newSubscribers = new Map(prev)
        if (!newSubscribers.has(sheetId)) {
          newSubscribers.set(sheetId, new Set())
        }
        newSubscribers.get(sheetId)!.add(callback)
        return newSubscribers
      })

      // Return unsubscribe function
      return () => {
        setSubscribers((prev) => {
          const newSubscribers = new Map(prev)
          const sheetSubscribers = newSubscribers.get(sheetId)
          if (sheetSubscribers) {
            sheetSubscribers.delete(callback)
            if (sheetSubscribers.size === 0) {
              newSubscribers.delete(sheetId)
            }
          }
          return newSubscribers
        })
      }
    },
    []
  )

  // Expose publishEvent globally so the existing project subscription can use it
  React.useEffect(() => {
    ;(window as any).publishEnrichmentEvent = publishEvent
    return () => {
      delete (window as any).publishEnrichmentEvent
    }
  }, [publishEvent])

  return (
    <EnrichmentEventsContext.Provider
      value={{ publishEvent, subscribeToSheet }}
    >
      {children}
    </EnrichmentEventsContext.Provider>
  )
}
